<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ToggleGroup from '@/components/filters/ToggleGroup.vue';

import '@/utils/floorUtils.ts';

import type { LocaleSchema } from '@/locales/index';
import { useUnit } from '@/composables/useUnit';
import { UnitStatusEnum } from '@/enums/UnitStatus';
import { ColorSelectOption } from '@/classes/ColorSelectOption';

const value = defineModel<string[]>({
  default: [],
});

const props = defineProps<{
  options: UnitStatusEnum[]
}>();

const { getStatusText, getStatusColor } = useUnit();

const { t } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});

const statusOptions = computed(() => {
  return props.options.map((option: UnitStatusEnum) => new ColorSelectOption(
    option.toString(),
    getStatusText(option),
    getStatusColor(option)
  ));
});
</script>

<template>
  <fieldset>
    <legend>{{ t('forms.units.filters.unitStatus.title') }}</legend>
    <ToggleGroup v-model="value"
                 class="toggleGroup_unitStatus"
                 :options="statusOptions" />
  </fieldset>
</template>

<style lang="scss">
.toggleGroup_unitStatus {
  .toggleGroup-label {
    --contrastColor: oklch(from var(--buttonBackgroundColor) calc(l * .4) c h);

    &[data-color] {
      border: 1px solid var(--contrastColor);
      color: var(--contrastColor);

      &:has(.toggleGroup-input:checked) {
        --lightnessModifierPressed: 1.16;
      }
    }

    &[data-color="green"] {
      --buttonBackgroundColor: var(--clrGreenStatus);
    }

    &[data-color="yellow"] {
      --buttonBackgroundColor: var(--clrYellowStatus);
    }

    &[data-color="red"] {
      --buttonBackgroundColor: var(--clrRedStatus);
    }

    &[data-color="orange"] {
      --buttonBackgroundColor: var(--clrOrangeStatus);
    }

    &[data-color="blue"] {
      --buttonBackgroundColor: var(--clrBlueStatus);
    }
  }
}
</style>
